import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Check, Loader2, AlertCircle } from 'lucide-react';
import Reveal from './Reveal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MailingList = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === 'loading') return;
    const trimmed = email.trim();
    if (!trimmed || !/.+@.+\..+/.test(trimmed)) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }
    setStatus('loading');
    setMessage('');
    try {
      const res = await axios.post(`${API}/mailing-list/subscribe`, {
        email: trimmed,
        source: 'ashthewitchtrials.co.uk'
      });
      setStatus('success');
      setMessage(res.data?.message || "You're on the list.");
      setEmail('');
    } catch (err) {
      setStatus('error');
      const detail = err?.response?.data?.detail;
      setMessage(
        typeof detail === 'string'
          ? detail
          : 'Something went wrong. Please try again in a moment.'
      );
    }
  };

  return (
    <section
      id="waitlist"
      aria-label="Sold out — join the mailing list for future ticket releases"
      className="relative py-16 md:py-24 px-6 md:px-10"
    >
      <div className="max-w-3xl mx-auto">
        <Reveal>
          {/* Sold out banner */}
          <div className="relative overflow-hidden border border-[#8b3a2a]/60 bg-gradient-to-b from-[#1a0d07] to-[#0f0806] px-6 md:px-10 py-10 md:py-12">
            {/* corner ornaments */}
            <span className="absolute top-2 left-2 w-8 h-8 border-l border-t border-[#c8935a]/40" />
            <span className="absolute top-2 right-2 w-8 h-8 border-r border-t border-[#c8935a]/40" />
            <span className="absolute bottom-2 left-2 w-8 h-8 border-l border-b border-[#c8935a]/40" />
            <span className="absolute bottom-2 right-2 w-8 h-8 border-r border-b border-[#c8935a]/40" />
            {/* candle glow */}
            <div
              className="absolute -inset-8 pointer-events-none opacity-40 flicker"
              style={{
                background:
                  'radial-gradient(circle at 50% 20%, rgba(220,140,60,0.18) 0%, transparent 60%)'
              }}
            />

            <div className="relative text-center">
              <p className="text-[10px] md:text-[11px] tracking-[0.5em] font-fell-sc text-[#c8935a] uppercase">
                Halloween 2026
              </p>
              <h2 className="mt-4 font-display text-4xl md:text-5xl text-[#e8dcc0] lowercase">
                sold out
              </h2>
              <div className="mt-6 divider-ornament max-w-md mx-auto">
                <span className="text-[9px] tracking-[0.4em] font-fell-sc">
                  waiting list
                </span>
              </div>
              <p className="mt-8 font-fell italic text-lg md:text-xl text-[#d9c8a3] leading-relaxed max-w-xl mx-auto">
                The current tickets have sold out. Sign up to the mailing list to
                be notified the moment further tickets are released.
              </p>

              <form
                onSubmit={onSubmit}
                className="mt-10 flex flex-col sm:flex-row items-stretch gap-3 max-w-xl mx-auto"
              >
                <div className="relative flex-1">
                  <Mail className="w-4 h-4 text-[#c8935a] absolute left-4 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    required
                    disabled={status === 'loading'}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.co.uk"
                    aria-label="Email address for the ASH ticket waiting list"
                    className="w-full h-12 pl-11 pr-4 bg-[#0a0705] border border-[#3a2818] text-[#e8dcc0] placeholder-[#5a4d3a] font-fell text-base focus:outline-none focus:border-[#c8935a] focus:ring-1 focus:ring-[#c8935a]/40 transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="h-12 px-8 border border-[#c8935a]/70 bg-black/30 hover:bg-[#c8935a]/10 text-[#e8dcc0] hover:text-[#f4e8ce] transition-all duration-500 font-fell-sc text-[11px] tracking-[0.4em] uppercase disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  {status === 'loading' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Adding
                    </>
                  ) : (
                    <>Notify me</>
                  )}
                </button>
              </form>

              <div className="mt-5 min-h-[24px]">
                {status === 'success' && (
                  <p className="inline-flex items-center gap-2 font-fell italic text-sm text-[#c8935a]">
                    <Check className="w-4 h-4" />
                    {message}
                  </p>
                )}
                {status === 'error' && (
                  <p className="inline-flex items-center gap-2 font-fell italic text-sm text-[#c67a5a]">
                    <AlertCircle className="w-4 h-4" />
                    {message}
                  </p>
                )}
              </div>

              <p className="mt-6 text-[10px] tracking-[0.3em] font-fell-sc text-[#5a4d3a] uppercase">
                No spam. Only ticket release alerts for ASH.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default MailingList;
